/**
 * The way into the admin portal (KPR-309): its own browser tab.
 *
 * The portal is its own UI (components/admin/AdminLayout.js, outside the app
 * shell), so the rail's Admin link — and, on a phone, the Me menu's — opens
 * it in a tab of its own, and opening it again goes back to THAT tab rather
 * than piling up another one. The tab is a named window, `keepr-admin`.
 *
 * Why not just `<a target="keepr-admin">`: a link click into a named window
 * that already exists NAVIGATES it, so the admin would lose the page they
 * were on. `window.open('', name)` instead hands back the existing window
 * untouched (a fresh one is `about:blank`, and only that one is pointed at
 * the portal), then it is focused. The anchor keeps the `href` and `target`,
 * so a middle-click, a modified click or "Open link in new tab" still does
 * what the browser always does.
 *
 * Never `rel="noopener"` here: a noopener window is not reachable by its name
 * from this tab, so every click would open another one. Both tabs are the
 * same origin, the same app.
 *
 * This file is eager (it is in the rail); nothing in it is admin code.
 */
import { ADMIN_WINDOW_NAME } from '../platform/adminWindow';
import { openAdmin } from '../platform';

export { ADMIN_WINDOW_NAME };

/** Where the portal opens: a reviewer's only section is Review. */
export function adminPortalPath({ admin }) {
	return admin ? '/admin' : '/admin/review';
}

/**
 * Opens the portal at `path` in the admin tab, or brings that tab forward
 * (the platform decides how: a named window on the web, the same view in the
 * app). With an `event` (a click on the rail's anchor), a modified or
 * non-primary click is left to the browser. Returns the window the portal is
 * in, or null.
 *
 * KPR-1668: the `window.open` itself lives in src/platform/adminWindow.js
 * (the web's implementation of `platform.openAdmin`); this file is what the
 * rail and the Me menu import.
 */
export function openAdminPortal(path, event) {
	return openAdmin(path, event);
}
